import { useState, useEffect, useCallback, useRef } from 'react';

import { getSingleton, putSingleton } from '../utils/visitDB';

/**
 * Cache-first (stale-while-revalidate) data loading for PWA read screens
 * (#2057). Paint the last-cached value instantly, then revalidate from the
 * network in the background and swap in fresh data. The spinner shows only when
 * there is nothing cached to display; an error surfaces only when both cache
 * AND network fail (a failed background revalidation never blows away good
 * cached data).
 *
 * The value is persisted in IndexedDB's `visit_singletons` store under the
 * given `cacheKey` (namespace your keys, e.g. `expenses:2026-06-05`). Pass a
 * null `cacheKey` to skip caching entirely (pure network).
 *
 * `deps` re-runs the load when they change (same semantics as a useEffect dep
 * array) — include anything the fetcher closes over (date, filters, etc.).
 */
export interface CachedResource<T> {
  /** The cached-or-fresh value, or null until the first paint. */
  data: T | null;
  /** True only while there is no value to show yet (first load, cold cache). */
  isLoading: boolean;
  /** True while a background revalidation is in flight (cache already shown). */
  isRevalidating: boolean;
  /** Set only when cache AND network both failed. */
  error: string | null;
  /** Re-run cache-read + revalidate (e.g. pull-to-refresh). */
  refetch: () => Promise<void>;
  /**
   * Optimistically update the in-memory value (and cache) without a fetch —
   * e.g. removing a row the user just deleted. The next revalidation reconciles
   * with the server.
   */
  mutate: (updater: (prev: T | null) => T | null) => void;
}

export function useCachedResource<T>(
  cacheKey: string | null,
  fetcher: () => Promise<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps: any[] = []
): CachedResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the latest fetcher without making it a dependency (callers usually
  // pass an inline closure that changes identity every render).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async () => {
    setError(null);
    let hadCache = false;

    if (cacheKey) {
      try {
        const cached = await getSingleton<T>(cacheKey);
        if (cached !== undefined && cached !== null) {
          setData(cached);
          setIsLoading(false);
          hadCache = true;
        }
      } catch {
        /* unusable cache — fall through to the network with the spinner up */
      }
    }
    if (!hadCache) setIsLoading(true);
    setIsRevalidating(true);

    try {
      const fresh = await fetcherRef.current();
      setData(fresh);
      if (cacheKey) {
        putSingleton(cacheKey, fresh).catch(() => {
          /* best-effort cache write */
        });
      }
    } catch (e) {
      // A failed background revalidation must not clobber good cached data —
      // only surface the error when we have nothing to show.
      if (!hadCache) {
        setError(e instanceof Error ? e.message : 'Failed to load');
      }
    } finally {
      setIsLoading(false);
      setIsRevalidating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, ...deps]);

  useEffect(() => {
    load();
  }, [load]);

  const mutate = useCallback(
    (updater: (prev: T | null) => T | null) => {
      setData((prev) => {
        const next = updater(prev);
        if (cacheKey && next !== null && next !== undefined) {
          putSingleton(cacheKey, next).catch(() => {
            /* best-effort cache write */
          });
        }
        return next;
      });
    },
    [cacheKey]
  );

  return { data, isLoading, isRevalidating, error, refetch: load, mutate };
}
